import type { DynamicPageProps } from '@/core/features/a-dynamic-page/dynamic-page-types';

import React from 'react';

import DividerHorizontalDesktop from '@/core/components/divider/divider-horizontal-desktop';
import { getComponentId } from '@/core/features/a-component/services/component-id-service';
import { GroupV1 } from '@/features/_index/mobile';
import getFromPageComponentRegistryDesktop from '@/features/a-dynamic-page/components/page-component-registry-desktop';
import PageComponentSwitch from '@/features/a-dynamic-page/components/page-component-switch';
import { validateLayoutPosition } from '@/features/page-layouts/layout-validation-service';

type PageComponentSwitchWrapperDesktopProps = DynamicPageProps & {
    divider?: boolean;
};

export default function PageComponentSwitchWrapperDesktop({
    divider = false,
    ...props
}: PageComponentSwitchWrapperDesktopProps) {
    const pageComponentNames = props.components.map((component) => {
        return component['@type'];
    });
    const firstVisibleComponentIndex = divider ? props.components.findIndex((c) => !c['@type'].includes('Data')) : -1;

    return (
        <>
            {props.components.map((component, index) => {
                validateLayoutPosition(component);

                const nextComponent = props.components[index + 1];
                const showDivider = divider && hasDivider(index, props, firstVisibleComponentIndex);
                const componentType = component['@type'];
                const componentConfig = getFromPageComponentRegistryDesktop(componentType);

                if (componentType === 'GroupV1') {
                    return (
                        <React.Fragment key={`${getComponentId(component['@id'])}-${index}`}>
                            <GroupV1
                                componentId={getComponentId(component['@id'])}
                                componentTypeFilter={props.ssrProps.componentTypeFilter}
                                components={component.attributes.components}
                                getComponentConfig={getFromPageComponentRegistryDesktop}
                                htmlTitle={component.attributes.htmlTitle}
                                isPageLoading={props.isPageLoading}
                                isWidget={props.ssrProps.isWidget}
                                link={component.attributes.link}
                                meta={component.meta}
                                pageComponentNames={pageComponentNames}
                                title={component.attributes.title}
                            />
                            {showDivider && (
                                <DividerHorizontalDesktop
                                    color={
                                        component?.meta.style === 'titled_border_box' ||
                                        (nextComponent?.['@type'] === 'GroupV1' &&
                                            nextComponent?.meta.style === 'titled_border_box')
                                            ? 'white'
                                            : 'alto'
                                    }
                                    marginBottom={0}
                                    marginTop={0}
                                />
                            )}
                        </React.Fragment>
                    );
                }

                return (
                    <React.Fragment key={`${getComponentId(component['@id'])}-${index}`}>
                        <PageComponentSwitch
                            component={component}
                            componentConfig={componentConfig}
                            componentIndex={index}
                            componentTypeFilter={props.ssrProps.componentTypeFilter}
                            isPageLoading={props.isPageLoading}
                            isWidget={props.ssrProps.isWidget}
                            pageComponentNames={pageComponentNames}
                        />
                        {showDivider && component.loading !== 'lazy' && (
                            <DividerHorizontalDesktop
                                color={
                                    nextComponent?.['@type'] === 'GroupV1' &&
                                    nextComponent.meta.style === 'titled_border_box'
                                        ? 'white'
                                        : 'alto'
                                }
                                marginBottom={0}
                                marginTop={0}
                            />
                        )}
                    </React.Fragment>
                );
            })}
        </>
    );
}

function hasDivider(
    index: number,
    props: PageComponentSwitchWrapperDesktopProps,
    firstVisibleComponentIndex: number,
): boolean {
    // Skip the last component
    if (index === props.components.length - 1) {
        return false;
    }

    const componentType = props.components[index]?.['@type'];

    // Skip HeadlineV1 since it is needs to be displayed along with the next component
    if (componentType === 'HeadlineV1') {
        return false;
    }

    // Skip BreadcrumbNavigationV1 since the divider under it is too noticeable
    if (componentType === 'BreadcrumbNavigationV1') {
        return false;
    }

    // Skip invisible components (checks current and next component)
    if (componentType?.includes('Data') || props.components[index + 1]?.['@type'].includes('Data')) {
        return false;
    }

    // Show divider skipping the first visible component in S0
    if (props.meta?.layoutId === 'Desktop_S0_V1') {
        return index > firstVisibleComponentIndex;
    }

    // On all other layouts, show divider after the first visible component
    return index >= firstVisibleComponentIndex;
}
